import './trigger.scss';
import { useEffect, useRef, useState } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import MapSelectSearchInput from './MapSelectSearchInput';
import PlaceSelectSearchInput from './PlaceSelectSearchInput';

interface PlaceFormProps {
  isSubmitted: boolean;
  selectedMap: any[];
  selectedPlace: any[];
  hasMap: any;
  hasPlace: any;
  mapOptions: any[];
  placesOptions: any[];
  onPlaceFormChange: Function;
  disabled: boolean;
  mapIsNull: boolean;
  onMapSearch: (query: string) => void;
  onMapPaginate: () => void;
  isLoadingMaps: boolean;
  hasMoreMaps: boolean;
  mapId: any;
}

export function PlaceForm({
  isSubmitted,
  selectedMap,
  selectedPlace,
  hasMap,
  hasPlace,
  onPlaceFormChange,
  disabled,
  mapOptions,
  placesOptions,
  mapIsNull,
  onMapSearch,
  onMapPaginate,
  isLoadingMaps,
  hasMoreMaps,
  mapId,
}: PlaceFormProps) {
  const [mapIsDirty, setMapDirty] = useState<boolean>(false);
  const [placeIsDirty, setPlaceDirty] = useState<boolean>(false);
  const [curMap, setCurMap] = useState<any>([]);
  const [curPlace, setCurPlace] = useState<any>([]);

  const pendingMapIdRef = useRef<string | null>(null);
  const typeaheadPlace = useRef(null);
  const effectiveMapId = curMap?.[0]?.id ?? mapId;
  const prevMapIdRef = useRef<string | null>(null);

  useEffect(() => {
    // If user picked/cleared a map, don't let parent overwrite curMap until it catches up
    if (pendingMapIdRef.current) {
      if (pendingMapIdRef.current === '__clearing__') {
        if (mapId == null) {
          pendingMapIdRef.current = null;
        } else {
          return;
        }
      } else {
        if (String(mapId) === pendingMapIdRef.current) {
          pendingMapIdRef.current = null;
        } else {
          return;
        }
      }
    }

    // Handle place sync safely based on map changes
    const nextMapId = effectiveMapId != null ? String(effectiveMapId) : null;
    const prevMapId = prevMapIdRef.current;

    const mapChanged = prevMapId !== null && nextMapId !== prevMapId;

    if (!nextMapId) {
      // map cleared -> always clear place
      setCurPlace([]);
    } else if (mapChanged) {
      // map changed -> clear place and DON'T rehydrate from selectedPlace
      setCurPlace([]);
    } else {
      // same map -> ok to sync place from parent
      setCurPlace(selectedPlace);
    }

    prevMapIdRef.current = nextMapId;

    // Only sync from parent when it matches the current mapId
    const parentId = selectedMap?.[0]?.id;
    if (parentId && String(parentId) === String(mapId)) {
      setCurMap(selectedMap);
      return;
    }

    if (mapId != null && (!curMap?.length || !curMap?.[0]?.id) && selectedMap?.length) {
      setCurMap(selectedMap);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMap, selectedPlace, mapId, effectiveMapId]);

  return (
    <>
      <Form.Group className="mb-3" as={Row}>
        <Form.Label column sm="2">
          Map
          <span className="text-danger"> *</span>
        </Form.Label>
        <Col>
          <InputGroup>
            <MapSelectSearchInput
              selectedOption={
                curMap?.[0] ? { value: Number(curMap[0].id), label: curMap[0].name } : null
              }
              onMapChange={(id, name) => {
                pendingMapIdRef.current = id != null ? String(id) : null;

                if (id == null) {
                  pendingMapIdRef.current = '__clearing__';

                  setCurMap([]);
                  setCurPlace([]);
                  onPlaceFormChange(null, 'MapId');
                  onPlaceFormChange(null, 'PlaceId');
                  return;
                }

                setCurMap([{ id: String(id), name }]);

                // clear place immediately when map changes
                setCurPlace([]);

                // (optional) if you still have a ref to a typeahead somewhere, otherwise remove
                typeaheadPlace.current?.clear?.();

                onPlaceFormChange(String(id), 'MapId');
                onPlaceFormChange(null, 'PlaceId');
              }}
            />

            {mapIsDirty && mapIsNull === true ? (
              <div className="invalid-feedback">* Please select a Map</div>
            ) : null}
          </InputGroup>
        </Col>
      </Form.Group>
      <Form.Group className="mb-3" as={Row}>
        <Form.Label column sm="2">
          Place
          <span className="text-danger"> *</span>
        </Form.Label>
        <Col>
          <InputGroup>
            <PlaceSelectSearchInput
              key={String(effectiveMapId ?? 'no-map')}
              mapId={effectiveMapId}
              disabled={!effectiveMapId}
              selectedOption={
                curPlace?.[0] ? { value: Number(curPlace[0].id), label: curPlace[0].name } : null
              }
              onPlaceChange={(id, name) => {
                if (id == null) {
                  setCurPlace([]);
                  onPlaceFormChange(null, 'PlaceId');
                  return;
                }
                setCurPlace([{ id: String(id), name }]);
                onPlaceFormChange(String(id), 'PlaceId');
              }}
            />

            {!hasPlace && isSubmitted ? (
              <div className="invalid-feedback">* Please select a Place</div>
            ) : null}
          </InputGroup>
        </Col>
      </Form.Group>
    </>
  );
}
